// Entry point: who is signed in, then the start page, "Meine Aufgaben", "Personen" or a board
// (WASM engine + sync + presence).
import { api, ApiError, loadSession, MeMenu, showDenied, showGone, showSignIn, takeReturn, type Session } from "./account";
import { App } from "./app";
import type { EngineModule } from "./engine-api";
import { Home } from "./home";
import { People } from "./people";
import { Radar } from "./radar";
import { AiAccess } from "./ai";
import { Connect } from "./connect";
import { Presence } from "./presence";
import { SharePanel } from "./share";
import { Store } from "./store";
import { SyncClient } from "./sync";
import { Tasks } from "./tasks";
import { FRAME_GAP, TEMPLATES } from "./templates";

const FONT = `"Roboto Flex", system-ui, sans-serif`;

/** `?board=<id>` opens a board; without it (or with an invalid id) the start page lists the boards. */
function boardFromUrl(): string | null {
  const b = new URLSearchParams(location.search).get("board");
  return b && /^[A-Za-z0-9_-]{1,64}$/.test(b) ? b : null;
}

/** Removes one-shot parameters from the address bar; returns their values. */
function takeParams(...names: string[]): Record<string, string | null> {
  const p = new URLSearchParams(location.search);
  const out: Record<string, string | null> = {};
  let changed = false;
  for (const n of names) {
    out[n] = p.get(n);
    if (p.has(n)) { p.delete(n); changed = true; }
  }
  const q = p.toString();
  if (changed) history.replaceState(null, "", q ? `${location.pathname}?${q}` : location.pathname);
  return out;
}

const expose = (v: Record<string, unknown>) => { (window as unknown as Record<string, unknown>).lanewerk = v; };

async function boot() {
  const session = await loadSession();
  const { login, welcome } = takeParams("login", "welcome");
  if (session.auth && !session.user) return showSignIn(login === "invalid");
  // the login link always lands on the start page: go back to where the person wanted to be
  if (welcome === "1") {
    const back = takeReturn();
    if (back) return location.replace(back);
  }
  const menu = new MeMenu(session);
  if (welcome === "setup") menu.welcome(() => { location.href = "?view=people"; });

  const board = boardFromUrl();
  if (!board) return startPage(session, menu);
  await openBoard(board, session, menu);
}

function startPage(session: Session, menu: MeMenu) {
  document.getElementById("lw-boot")?.remove();
  document.body.classList.add("is-home");
  let view = new URLSearchParams(location.search).get("view");
  if (view === "people" && !session.user?.admin) view = null;
  const ids = { tasks: ["lw-tasks", "lw-nav-tasks"], radar: ["lw-radar", "lw-nav-radar"], people: ["lw-people-page", "lw-nav-people"], ai: ["lw-ai-page", ""], connect: ["lw-connect-page", ""] } as Record<string, string[]>;
  const [main, nav] = ids[view ?? ""] ?? ["lw-home", "lw-nav-boards"];
  document.getElementById(main)!.hidden = false;
  if (nav) document.getElementById(nav)!.setAttribute("aria-current", "page");
  if (view === "tasks") expose({ tasks: new Tasks(menu.me.short), session });
  else if (view === "radar") expose({ radar: new Radar(), session });
  else if (view === "people") expose({ people: new People(session.user!), session });
  else if (view === "ai") expose({ ai: new AiAccess(session), session });
  else if (view === "connect") expose({ connect: new Connect(session), session });
  else expose({ home: new Home(), session });
}

async function openBoard(board: string, session: Session, menu: MeMenu) {
  const status = document.getElementById("lw-boot")!;
  // one-shot URL parameters: a new board's title, a card to focus (from "Meine Aufgaben")
  const { title: newTitle, card: focusCard, template, kind: newKind } = takeParams("title", "card", "template", "kind");
  try {
    // the generated glue lives next to app.js (copied by build.mjs from web/src/wasm)
    const wasmUrl = new URL("./wasm/lanewerk.js", import.meta.url).href;
    const mod = (await import(/* webpackIgnore: true */ wasmUrl)) as EngineModule;
    await mod.default({ module_or_path: new URL("./wasm/lanewerk_bg.wasm", import.meta.url) });
    await Promise.race([document.fonts.load(`12px "Roboto Flex"`), new Promise((r) => setTimeout(r, 1500))]).catch(() => {});
    const peer = Math.floor(Math.random() * 2 ** 52) + 1;
    const store = new Store(new mod.Engine(peer));
    const app = new App(store, board, FONT);
    app.bindEditor();
    const proto = location.protocol === "https:" ? "wss" : "ws";
    let first = true;
    const sync = new SyncClient(`${proto}://${location.host}/ws/${board}`, store, (s) => {
      app.setSyncStatus(s);
      if (s === "online" && first) {
        first = false;
        if (newTitle && !store.boardTitle()) {
          store.setBoardTitle(newTitle);
          // a new team board (doc 13); later board admins change the kind on the start page
          if (newKind === "team") store.setBoardKind("team");
        }
        // a new board from a template: its frames, side by side (only while the board is empty)
        const t = template ? TEMPLATES[template] : undefined;
        if (t && !store.readOnly && store.frames().length === 0 && store.cardCount() === 0) {
          let x = 0;
          t.frames().forEach((layout, i) => {
            const id = `f${Date.now().toString(36)}${i}`;
            if (store.addFrame(id, x, 0, layout)) {
              const f = store.frames().find((fr) => fr.id === id);
              x += (f ? f.cols.reduce((a, c) => a + c.width, 0) : 0) + FRAME_GAP;
            }
          });
          app.fitToContent();
        }
        // a deep link to a card wins; else the board's default view, if it has one
        const view = store.defaultView();
        if (focusCard) app.focusCard(focusCard);
        else if (view) app.showRect([view.x, view.y, view.w, view.h]);
      }
    });
    app.sync = sync;
    sync.onRole = (r) => app.setReadOnly(r.role === "read");
    // refused socket: board in the trash, signed out meanwhile, or no access (else: just offline)
    sync.onRefused = () => {
      api(`./api/boards/${encodeURIComponent(board)}/access`).catch((e) => {
        if (!(e instanceof ApiError)) return;
        if (e.status === 410) { sync.stop(); showGone(); }
        else if (!session.auth) return;
        else if (e.status === 401) { sync.stop(); showSignIn(false); }
        else if (e.status === 403) { sync.stop(); showDenied(); }
      });
    };
    sync.onGone = () => showGone();
    const presence = new Presence(peer, (bytes) => sync.sendPresence(bytes), menu.me);
    menu.onChange((me) => presence.setMe(me));
    sync.onPresence = (bytes) => presence.receive(bytes);
    sync.onSynced = () => presence.announce();
    app.attachPresence(presence);
    if (session.user) {
      const shareBtn = document.getElementById("lw-share")!;
      const share = new SharePanel(board, session.user, () => app.focus());
      shareBtn.hidden = false;
      shareBtn.addEventListener("click", () => {
        if (share.isOpen) return share.close();
        app.closePanels();
        void share.open();
      });
    }
    let fitted = focusCard !== null;
    store.subscribe((source) => {
      // first content from the server: show all of it
      // (not while following a presentation: that decides the view)
      if (!fitted && source === "remote" && store.bounds()) { fitted = true; if (!app.isFollowing) app.fitToContent(); }
    });
    sync.start();
    status.remove();
    // small API for the browser console and the end-to-end tests
    expose({ store, sync, app, board, peer, presence, session });
  } catch (e) {
    status.textContent = `Lanewerk konnte nicht starten: ${(e as Error).message}`;
    status.classList.add("error");
    console.error(e);
  }
}

void boot();
